import React, { useEffect, useState } from 'react'
import { Button, ButtonGroup, Checkbox, Grid, Icon, IconButton, makeStyles } from '@cicero/ui'
import { DataFaker } from '@cicero/data/utils'
import { DateTime } from 'luxon'
import { PageDoc, PageInboxStatus, PageSchema, useModel } from 'models'
import { SafeEditor } from 'editor'

export const InboxPage = () => {
  const { pageWrapper, inboxWrapper, inboxListWrapper, inboxContentWrapper, inboxToolbar } = styles()
  const groups = exampleInboxGroups
  const Pages = useModel(PageSchema)
  const [debugGroup, setDebugGroup] = useState<IInboxGroup>({ title: 'Today', items: [] })
  const [activeItem, setActiveItem] = useState<IInboxItem|null>(null)

  useEffect(() => {
    const sub = Pages.QUERY$({ selector: { inbox_status: PageInboxStatus.pending } }).subscribe(pages => {
      setDebugGroup({ ...debugGroup, items: pages.map(p => ({ name: p.title, page: p, subtitle: 'Is Due Today' })) })
    })

    return () => sub.unsubscribe()
  }, [])

  return (
    <div className={pageWrapper}>
      <div className={inboxToolbar}>
        <ButtonGroup color="secondary">
          <Checkbox />
          <Button>Archive</Button>
        </ButtonGroup>
      </div>
      <Grid className={inboxWrapper}>
        <Grid item xs={4} className={inboxListWrapper}>
          <InboxItemGroup group={debugGroup} onClickItem={(item) => setActiveItem(item)} />
          { groups.map((group, i) => <InboxItemGroup key={i} group={group} />) }
        </Grid>
        <Grid item xs={8} className={inboxContentWrapper}>
          { activeItem && activeItem.page
            ? <SafeEditor page={activeItem.page} key={activeItem.page.id} />
            : null
          }
        </Grid>
      </Grid>
    </div>
  )
}

export const InboxItemGroup = ({ group, onClickItem }: { group: IInboxGroup, onClickItem?: (item: IInboxItem) => void }) => {
  const { inboxGroupWrapper, inboxGroupItemsWrapper } = styles()

  return (
    <div className={inboxGroupWrapper}>
      <header>{group.title} ({group.items.length})</header>
      <div className={inboxGroupItemsWrapper}>
        {group.items.map((item, i) => <InboxItem key={i} item={item} onClick={() => onClickItem && onClickItem(item)} />)}
      </div>
    </div>
  )
}

export const InboxItem = ({ item, onClick }: { item: IInboxItem, onClick?: (item: IInboxItem) => void }) => {
  const { inboxItemWrapper, inboxTextWrapper, inboxTitleWrapper } = styles()
  return (
    <div className={inboxItemWrapper}>
      <Checkbox contextMenu="menu" />
      <div className={inboxTextWrapper} onClick={() => onClick && onClick(item)}>
        <div className={inboxTitleWrapper}>
          <strong>{ item.name }</strong>
          <ButtonGroup className="inbox-item-buttons" size="small">
            <IconButton><Icon>archive</Icon></IconButton>
            <IconButton><Icon>schedule</Icon></IconButton>
          </ButtonGroup>
        </div>
        { item.subtitle ? <caption>{item.subtitle}</caption> : null }
      </div>
    </div>
  )
}

const styles = makeStyles(({ palette }) => ({
  pageWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    height: '100%',
  },
  inboxWrapper: {
    width: '100%',
    display: 'flex',
    flexGrow: 1,
    height: '100%',
  },
  inboxListWrapper: {
    borderRight: '1px solid #CCC',
    overflowY: 'auto',
  },
  inboxContentWrapper: {
    overflowY: 'auto',
  },
  inboxToolbar: {
    background: palette.primary.main,
    color: `${palette.getContrastText(palette.primary.main)} !important`,
    width: '100%',
    minHeight: 60,
    alignItems: 'center',
    display: 'flex',
  },
  inboxGroupWrapper: {
    background: palette.primary.main,
    color: palette.getContrastText(palette.primary.main),

    '& header': {
      fontSize: 18,
      fontWeight: 'bold',
      padding: 12,
    },
  },
  inboxGroupItemsWrapper: {

  },
  inboxItemWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    borderBottom: '1px solid #CCC',
    background: palette.background.default,
    color: palette.getContrastText(palette.background.default),
    cursor: 'pointer',

    '& .inbox-item-buttons': {
      opacity: 0,
      transition: 'all ease-in-out .2s',

      '& > *': {},
    },

    '&:hover': {
      '& .inbox-item-buttons': {
        opacity: 1,
      },
    },
  },
  inboxTextWrapper: {
    padding: '12px 0',
    flexGrow: 1,

    '& > caption': {
      fontSize: 12,
      color: palette.text.hint,
      width: '100%',
      display: 'flex',
    },
  },
  inboxTitleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
}))

const exampleInboxGroups = Array(15)
  .fill(DateTime.local())
  .map((now: DateTime, i) => {
    const newTime = now.plus({ days: i })
    const key = newTime.toRelativeCalendar() || 'Today'
    return {
      title: key,
      items: DataFaker.genFactory((seed) => ({
        name: [DataFaker.VERBS, DataFaker.ADJECTIVES, DataFaker.NOUNS].map(items => DataFaker.gen(items, DataFaker.genSeed(key) + seed)).join(' '),
        page: null,
      }), DataFaker.gen(DataFaker.DATE_MONTHS, DataFaker.genSeed(key))),
    }
  })

export interface IInboxItem {
  name: string
  page: PageDoc|null
  subtitle?: string
}

export interface IInboxGroup {
  title: string
  items: IInboxItem[]
}
